import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Handles the return leg of Emergent Google Auth.
 * Emergent redirects to `<origin>/auth/callback#session_id=<sid>`. We pull
 * the sid out of the URL fragment, POST it to /api/auth/google/session,
 * and — on success — land the user on /dashboard. On failure (email not
 * on the allowlist, expired session, network error) we show the reason.
 */
export const AuthCallback = () => {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || '';
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const sid = params.get('session_id');
      if (!sid) {
        setError("Google sign-in didn't return a session. Please try again.");
        return;
      }
      try {
        await googleSession(sid);
        // Strip the fragment so a page-reload doesn't retry the exchange
        window.history.replaceState({}, '', '/dashboard');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || 'Sign-in failed.';
        setError(detail);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
         style={{ backgroundColor: '#F4F0E6' }}>
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center"
           data-testid="auth-callback">
        <div className="mb-4 inline-flex items-center gap-2 text-primary">
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-semibold uppercase tracking-widest">Epicurea Nexus</span>
        </div>
        {error ? (
          <>
            <div className="text-ochre mb-3 flex justify-center">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-serif mb-2">Sign-in blocked</h1>
            <p className="text-sm text-text-muted mb-6" data-testid="auth-callback-error">
              {error}
            </p>
            <button
              onClick={() => (window.location.href = '/login')}
              className="w-full bg-primary text-white py-2 rounded-md text-sm font-semibold"
              data-testid="auth-callback-back">
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-4 text-primary" />
            <h1 className="text-lg font-serif mb-2">Finishing sign-in…</h1>
            <p className="text-sm text-text-muted">Exchanging your Google session.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
